import React from 'react';
import { Translate, I18n } from 'react-redux-i18n';
import Masonry from 'masonry-layout';

import { UserDTO } from '../../../../network/api/types/compte/UserDTO';
import Button from '../../../common/Button';
import StructureBox from './StructureBox';
import rattachementDemandApi from '../../../../network/api/rattachementDemandApi';
import { EntityDTO } from '../../../../network/api/types/compte/EntityDTO';
import {
  notifyErrorWithMessage, notifySuccess, toastWarning,
} from '../../../common/Notification';
import { RattachementDemand } from '../../../../network/api/types/RattachementDemand';
import { Entity } from '../../../../network/api/types/compte/Entity';
import FindStructureModal from '../../accountCreation/components/FindStructureModal';
import { getStructureName, getStructureNameV2 } from '../../../../services/utils';

type Props = {
  myUserDetails: UserDTO,
  openDrawer: (route: string, isManager?: boolean) => void,
  updateNeededByEntity: Map<string, number>,
  updateCollaborateurStatut?: (entity: Entity) => void,
};

type State = {
  isModalOpen: boolean,
  myRattachementDemands: RattachementDemand[],
  myEntitiesModified?: Array<Entity>
};

const columnWidth = 590;
const gutter = 20;

class MyProInfo extends React.Component<Props, State> {
  state: State = {
    isModalOpen: false,
    myRattachementDemands: [],
    myEntitiesModified: undefined,
  };

  masonRef = React.createRef<HTMLDivElement>();

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    const previousEntities = prevState.myEntitiesModified
      ? prevState.myEntitiesModified
      : prevProps.myUserDetails?.entities;
    const currentEntities = this.state.myEntitiesModified
      ? this.state.myEntitiesModified
      : this.props.myUserDetails?.entities;
    if (currentEntities !== previousEntities) {
      this.updateMasonry();
    }
  }

  componentDidMount(): void {
    this.getUnvalidatedDemands();
  }

  updateMasonry = (): void => {
    const masonry = new Masonry(this.masonRef.current, {
      columnWidth,
      gutter,
      transitionDuration: 0,
    });
  };

  getUpdateNeededFromEntity = (entity: Entity): number => {
    if (entity
      && this.props.updateNeededByEntity
      && this.props.updateNeededByEntity.get(entity.id)
    ) {
      return this.props.updateNeededByEntity.get(entity.id);
    }
    return 0;
  };

  getUnvalidatedDemands = (): void => {
    rattachementDemandApi.getCurrentUserDemands()
      .then((myRattachementDemands) => this.setState({ myRattachementDemands }))
      .finally(() => this.updateMasonry());
  };

  openModal = (): void => {
    this.setState((prevState) => ({ isModalOpen: !prevState.isModalOpen }));
  };

  removeRattachement = (id: string): void => {
    this.setState((prevState: State) => {
      const myEntities = prevState.myEntitiesModified
        ? prevState.myEntitiesModified
        : this.props.myUserDetails.entities;
      return ({ myEntitiesModified: myEntities?.filter((entity: Entity) => entity.id !== id) });
    });
  };

  saveStructure = (entity: EntityDTO): void => {
    this.openModal();
    rattachementDemandApi.create({
      entiteOu: entity.ou,
      personneUid: this.props.myUserDetails.idn,
      mailActive: true,
    })
      .then(() => {
        notifySuccess(`${I18n.t('HOME.DEMANDED_RATTACHEMENT')} ${getStructureNameV2(entity)}.`);
        this.getUnvalidatedDemands();
      })
      .catch((err) => {
        if (typeof err.then === 'function') {
          err.then((e) => {
            if (e.description === 'duplicate.rattachement.demande') {
              toastWarning(I18n.t('HOME.DUPLICATE_RATTACHEMENT_DEMAND'));
            } else {
              notifyErrorWithMessage(I18n.t('HOME.FAIL_RATTACHEMENT_DEMAND'));
            }
          });
        } else {
          notifyErrorWithMessage(I18n.t('HOME.FAIL_RATTACHEMENT_DEMAND'));
        }
      });
  };

  entitiesSorter = (entityA: Entity, entityB: Entity): number => {
    if (entityA.rattachement.linkType === 'Collaborateur') {
      if (entityB.rattachement.manager) {
        return 1;
      }
    } else if (!entityB.rattachement.manager) {
      return -1;
    }
    const nameA = getStructureName(entityA);
    const nameB = getStructureName(entityB);
    return nameA.localeCompare(nameB);
  };

  rattachementDemandSorter = (demandA: RattachementDemand, demandB: RattachementDemand): number =>
    demandA.shortCompanyName.localeCompare((demandB.shortCompanyName));

  render(): React.ReactElement {
    const myEntities = this.state.myEntitiesModified
      ? this.state.myEntitiesModified
      : this.props.myUserDetails?.entities;
    return (
      <div className="manage-pro-info">
        <div className="title-wrapper">
          <FindStructureModal
            openModal={this.openModal}
            isModalOpen={this.state.isModalOpen}
            saveStructure={this.saveStructure}
            selectedStructureIds={
              (myEntities
                ?.map((entity) => entity.id) || [])
                .concat(this.state.myRattachementDemands
                  .map((rattachementDemand) => rattachementDemand.ouEntite) || [])
            }
          />
          <h3 className="home-title">
            <Translate value="HOME.MANAGE_PRO_INFO" />
            <Button
              type="secondary"
              onClick={this.openModal}
            >
              <Translate
                value={myEntities?.length
                  ? 'HOME.FIND_OTHER_STRUCTURE' : 'HOME.FIND_STRUCTURE'}
              />
            </Button>
          </h3>
          <div ref={this.masonRef} className="professional-info-boxes">
            {((
              myEntities?.filter((e) => !e.rattachement.blocked)
                .sort(this.entitiesSorter)
                .map((entity) => (
                  <StructureBox
                    key={entity.id}
                    entity={entity}
                    openDrawer={this.props.openDrawer}
                    isManager={entity.rattachement.manager}
                    updateNeeded={this.getUpdateNeededFromEntity(entity)}
                    updateCollaborateurStatut={() => this.props.updateCollaborateurStatut(entity)}
                    idn={this.props.myUserDetails.idn}
                    removeRattachement={this.removeRattachement}
                  />
                ))
            ) || []).concat((
              this.state.myRattachementDemands?.sort(this.rattachementDemandSorter)
                .map((rattachementDemand) => (
                  <StructureBox
                    key={rattachementDemand.id}
                    structureName={rattachementDemand.shortCompanyName}
                    rattachement={rattachementDemand}
                    reloadList={this.getUnvalidatedDemands}
                  />
                ))
            ) || []).concat((
              myEntities?.filter((e) => e.rattachement.blocked)
                .map((entity) => (
                  <StructureBox
                    key={entity.id}
                    entity={entity}
                    isBlock
                    updateNeeded={this.getUpdateNeededFromEntity(entity)}
                    updateCollaborateurStatut={() => this.props.updateCollaborateurStatut(entity)}
                  />
                ))
            ))}
            {!myEntities?.length
            && !this.state.myRattachementDemands?.length
            && (
              <Translate value="HOME.NO_LINKED_STRUCTURE" />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default MyProInfo;
